import ColumnHeaderWithSearch from "./SearchBar";
import { 
  Box, 
  Button, 
  IconButton, 
  Menu, 
  MenuItem, 
  ListItemIcon, 
  ListItemText,
  Chip
} from "@mui/material";
import { handleDelte, handleGenerateLink } from "./helperFn";
import CommonTooltip from "../../../components/CommonTooltip";

import { GridDeleteIcon } from "@mui/x-data-grid";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import "./DashboardColumn.css";

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export const userColumns = (
  setModalOpen,
  setTestLink,
  searchTerms,
  setSearchTerms,
  licenses,
  handleOpenConfirmationDialog,
  selectedIds,
  userMasterId,
  handleGenerateReport,
  // Add these new parameters for menu handling
  anchorEl,
  selectedUserId,
  handleMenuClick,
  handleMenuClose,
  // Add email functionality parameters
  setEmailModalOpen,
  setEmailData,
  coachName,
  userRole // Add userRole parameter to check if user is coach
) => {
  const handleSearchChange = (field) => (value) => {
    setSearchTerms((prev) => ({ ...prev, [field]: value }));
  };

  const addSearchableHeader = (col) => ({
    ...col,
    sortable: false,
    renderHeader: () => (
      <ColumnHeaderWithSearch
        title={col.headerName}
        value={searchTerms[col.field]}
        onChange={handleSearchChange(col.field)}
        align={col.align || "left"}
      />
    ),
  });

  const baseCols = [
    {
      field: "USER_NAME",
      headerName: "Username",
      minWidth: 200,
      flex: 1,
      align: "left",
      headerAlign: "left",
      headerClassName: "sticky-header",
      cellClassName: "sticky-cell",
      renderCell: (params) => (
        <div className="sticky-cell">{params.row.USER_NAME}</div>
      ),
    },
    { 
      field: "USER_AGE", 
      headerName: "Age", 
      minWidth: 200,
      flex: 1,
      align: "right",
      headerAlign: "right",
    },
    { 
      field: "USER_TYPE", 
      headerName: "Type", 
      minWidth: 200, 
      flex: 1,
      align: "left",
      headerAlign: "left",
    },
    { 
      field: "USER_COURSE", 
      headerName: "Course", 
      minWidth: 180, 
      flex: 1,
      align: "left",
      headerAlign: "left",
    },
    { 
      field: "USER_GRADE", 
      headerName: "Grade", 
      minWidth: 100, 
      flex: 1,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "USER_COMPANY_HIGHER_EDUCATION",
      headerName: "Education",
      minWidth: 200,
      flex: 1,
      align: "left",
      headerAlign: "left",
    },
    { 
      field: "INSTITUTION_NAME", 
      headerName: "Institution", 
      minWidth: 220, 
      flex: 1,
      align: "left",
      headerAlign: "left",
    },
    { 
      field: "USER_EMAIL", 
      headerName: "Email", 
      minWidth: 300, 
      flex: 1,
      align: "left",
      headerAlign: "left",
    },
    { 
      field: "USER_PHONE_NO", 
      headerName: "Phone", 
      minWidth: 200, 
      flex: 1,
      align: "left",
      headerAlign: "left",
    },
    { 
      field: "COMPANY_NAME", 
      headerName: "Company", 
      minWidth: 200, 
      flex: 1,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const hasResponse = params.row.USER_RESPONCE?.value;
        const isActive = !hasResponse;
        
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Chip
              label={isActive ? "Active" : "Completed"}
              color={isActive ? "success" : "default"}
              size="small"
              variant="outlined"
              sx={{
                fontWeight: 500,
                fontSize: '0.75rem',
                '&.MuiChip-outlined': {
                  borderColor: isActive ? '#4caf50' : '#9e9e9e',
                  color: isActive ? '#4caf50' : '#9e9e9e',
                }
              }}
            />
          </Box>
        );
      },
    },
  ];

  const searchColumns = baseCols.map((col) =>
    addSearchableHeader({
      ...col,
      flex: 1,
      disableColumnMenu: true,
      filterable: true,
    })
  );

  return [
    ...searchColumns,
    {
      field: "CREATED_ON",
      headerName: "Created On",
      flex: 1,
      minWidth: 120,
      disableColumnMenu: true,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => <span>{formatDate(params.row.CREATED_ON)}</span>,
    },
    {
      field: "testLink",
      headerName: "Test Link",
      flex: 1,
      minWidth: 160,
      disableColumnMenu: true,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",

      renderCell: (params) => {
        const hasResponse = params.row.USER_RESPONCE != null;
        const hasLink = params.row.TOKEN != null;

        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
            }}
          >
            {!hasResponse ? (
              <>
                <CommonTooltip 
                  title="Generate Link"
                  placement="top"
                >
                  <span>
                    <IconButton
                      size="small"
                      variant="contained"
                      color="secondary"
                      onClick={() =>
                        handleGenerateLink(
                          params.row.id,
                          setModalOpen,
                          setTestLink,
                          userMasterId
                        )
                      }
                      disabled={licenses.remaining === 0 || selectedIds.length > 1}
                      sx={{ 
                        bgcolor: 'secondary.main', 
                        color: 'white',
                        '&:hover': { bgcolor: 'secondary.dark' },
                        '&:disabled': { bgcolor: 'grey.400' }
                      }}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </span>
                </CommonTooltip>
                <CommonTooltip 
                  title="Send Email"
                  placement="top"
                >
                  <span>
                    <IconButton
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        setEmailData({
                          to: params.row.USER_EMAIL,
                          subject: "Your Test Link - Find Your True North",
                          message: `Hello ${params.row.USER_NAME},

You have been invited to take the Find Your True North assessment. This assessment will help you discover your interests, personality traits, and values to guide your career and life decisions.

Please click on the link below to start your assessment:

[TEST_LINK_WILL_BE_ADDED_AUTOMATICALLY]

The assessment will take approximately 30-45 minutes to complete. Please ensure you have a quiet environment and can complete it in one sitting.

Best regards,
Find Your True North Team
${coachName || 'Coach'}`
                        });
                        setEmailModalOpen(true);
                      }}
                      sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white',
                        '&:hover': { bgcolor: 'primary.dark' },
                        '&:disabled': { bgcolor: 'grey.400', color: 'grey.600' }
                      }}
                    >
                      <EmailIcon />
                    </IconButton>
                  </span>
                </CommonTooltip>
              </>
            ) : (
              <IconButton
                size="small"
                variant="contained"
                color="secondary"
                disabled
                title="Test Submitted"
                sx={{ 
                  bgcolor: 'grey.400', 
                  color: 'white'
                }}
              >
                <ContentCopyIcon />
              </IconButton>
            )}
          </Box>
        );
      },
    },
    {
      field: "report",
      headerName: "Report",
      flex: 1,
      minWidth: 140,
      disableColumnMenu: true,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const hasResponse = params.row.USER_RESPONCE == null;
        // Check if user type supports reports - SCHOOL and COLLEGE users can access reports
        const isReportSupportedUser = ['SCHOOL', 'COLLEGE'].includes(params.row.USER_TYPE);
        // Check if current user is a coach
        const isCoach = userRole === 'COACH';
        // Disable report functionality for unsupported user types when logged in as coach
        const isReportDisabled = isCoach && !isReportSupportedUser;

        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CommonTooltip 
              title={isReportDisabled ? "Reports are only available for SCHOOL and COLLEGE users" : hasResponse ? "No report available" : "Report Actions"}
              placement="top"
            >
              <span>
                <IconButton
                  aria-label="report-actions"
                  disabled={hasResponse || isReportDisabled}
                  onClick={(e) => handleMenuClick(e, params.row.id)}
                >
                  <MoreVertIcon />
                </IconButton>
              </span>
            </CommonTooltip>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl) && selectedUserId === params.row.id}
              onClose={handleMenuClose}
            >
              <MenuItem
                onClick={() => {
                  handleGenerateReport(params.row.id, "download");
                  handleMenuClose();
                }}
                disabled={isReportDisabled}
              >
                <ListItemIcon>
                  <DownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Download Report</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleGenerateReport(params.row.id, "regenerate");
                  handleMenuClose();
                }}
                disabled={isReportDisabled}
              >
                <ListItemIcon>
                  <RefreshIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Regenerate Report</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleGenerateReport(params.row.id, "email", params.row);
                  handleMenuClose();
                }}
                disabled={isReportDisabled}
              >
                <ListItemIcon>
                  <EmailIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Email Report</ListItemText>
              </MenuItem>

            </Menu>
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 140,
      disableColumnMenu: true,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const hasResponse = params.row.USER_RESPONCE !== null;
        console.log("RESPONSE ", params.row.USER_RESPONCE)
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <IconButton
              size="small"
              variant="outlined"
              color="warning"
              disabled={hasResponse || selectedIds.length > 1}
              onClick={() => {
                handleOpenConfirmationDialog(params.row.id);
              }}
            >
              <GridDeleteIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];
};